import auth0 from 'auth0-js'
import { AUTH_CONFIG } from './auth0-variables'
import Swal from 'sweetalert2'

class Auth {
  accessToken
  idToken
  expiresAt
  userProfile
  tokenRenewalTimeout

  auth0 = new auth0.WebAuth({
    domain: AUTH_CONFIG.domain,
    clientID: AUTH_CONFIG.clientID,
    redirectUri: AUTH_CONFIG.callbackUrl,
    audience: AUTH_CONFIG.apiUrl,
    responseType: 'token id_token',
    scope: 'openid profile'
  })

  constructor() {
    this.login = this.login.bind(this)
    this.logout = this.logout.bind(this)
    this.handleAuthentication = this.handleAuthentication.bind(this)
    this.isAuthenticated = this.isAuthenticated.bind(this)
    this.getAccessToken = this.getAccessToken.bind(this)
    this.getIdToken = this.getIdToken.bind(this)
    this.renewSession = this.renewSession.bind(this)
    this.getProfile = this.getProfile.bind(this)
    this.getExpiryDate = this.getExpiryDate.bind(this)
    this.scheduleRenewal()
  }

  getAccessToken() {
    return this.accessToken
  }

  getIdToken() {
    return this.idToken
  }

  getProfile() {
    return new Promise((resolve, reject) => {
      this.auth0.client.userInfo(this.accessToken, (err, profile) => {
        if (err) return reject(err)
        if (!profile) {
          return reject(err)
        }

        this.userProfile = profile
        resolve()
      })
    })
  }

  getExpiryDate() {
    return JSON.stringify(new Date(this.expiresAt))
  }

  isAuthenticated() {
    // Check whether the current time is past the
    // access token's expiry time
    let expiresAt = this.expiresAt
    return new Date().getTime() < expiresAt
  }

  login(referrer) {
    this.auth0.authorize({
      redirectUri: `${AUTH_CONFIG.callbackUrl}?referrer=${referrer}`
    })
  }

  handleAuthentication() {
    return new Promise((resolve, reject) => {
      this.auth0.parseHash((err, authResult) => {
        if (err) return reject(err)
        if (!authResult || !authResult.idToken) {
          return reject(err)
        }

        this.setSession(authResult)
        resolve()
      })
    })
  }

  setSession(authResult) {
    // Set isLoggedIn flag in localStorage
    localStorage.setItem('isLoggedIn', 'true')

    // Set the time that the access token will expire at
    let expiresAt = authResult.expiresIn * 1000 + new Date().getTime()
    this.accessToken = authResult.accessToken
    this.idToken = authResult.idToken
    this.expiresAt = expiresAt

    // schedule a token renewal
    this.scheduleRenewal()
  }

  renewSession() {
    return new Promise((resolve, reject) => {
      this.auth0.checkSession({}, (err, authResult) => {
        // This checks if the user is going to be logged out because of third party cookies.
        // If the user is logged in and the session is not expired, then it is a problem of auth0 cookies that get rejected
        if (
          err &&
          localStorage.getItem('isLoggedIn') === 'true' &&
          this.expiresAt - Date.now() > 0
        ) {
          Swal.fire({
            title: 'Error!',
            text: 'You must enable third party cookies.',
            icon: 'error',
            confirmButtonText: 'Ok'
          }).then(() => {
            this.logout()
            return reject(err)
          })
        } else {
          if (err) {
            this.logout()
            return reject(err)
          }
          if (!authResult || !authResult.idToken) {
            this.logout()
            return reject(err)
          }

          this.setSession(authResult)
          resolve()
        }
      })
    })
  }

  logout() {
    // Remove tokens, expiry time and userProfile
    this.accessToken = null
    this.idToken = null
    this.expiresAt = 0
    this.userProfile = null

    // Clear token renewal
    clearTimeout(this.tokenRenewalTimeout)

    // Remove isLoggedIn flag from localStorage
    localStorage.removeItem('isLoggedIn')

    this.auth0.logout({
      returnTo: window.location.origin
    })
  }

  scheduleRenewal() {
    let expiresAt = this.expiresAt
    const timeout = expiresAt - Date.now()
    if (timeout > 0) {
      this.tokenRenewalTimeout = setTimeout(() => {
        this.renewSession().then(() => {
          return
        })
      }, timeout)
    }
  }
}

const auth = new Auth()

export default auth
